import { AddIcon, MinusIcon } from "@chakra-ui/icons"
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    ButtonGroup,
    Divider,
    HStack,
    Icon,
    SimpleGrid,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Text,
    Tooltip,
    useBreakpointValue,
} from "@chakra-ui/react"
import { Liquidity, MarketStatus, RangeType } from "@perp/sdk-curie"
import Big from "big.js"
import { ModalType } from "component/Modal/type"
import { MotionVStack } from "component/Motion"
import { Stats } from "component/Stats"
import { useModal } from "container/modal"
import { useMemo } from "react"
import { RiBubbleChartLine, RiContrastDrop2Fill } from "react-icons/ri"
import { usePositionMakerImpermanent } from "sdk-react/clearingHouse/usePositionMakerImpermanent"
import { useMarket } from "sdk-react/markets/useMarket"

import { useLiquidityDetailStrings } from "../../hook/useLiquidityDetailString"

interface LiquidityCardProps {
    liquidity: Liquidity
}

function getPrecisionByDigits(value: number) {
    // determine how many digits below zero
    if (value <= 1) {
        // 1.234567 -> 1.234 like small token ONE or CRO, we need to show more digits below zero
        return 4
    }
    if (value <= 2) {
        // 12.34567 -> 12.345
        return 2
    }
    if (value <= 3) {
        // 123.4567 -> 123.45
        return 2
    }
    if (value <= 4) {
        // 1234.567 -> 1234.5 like ETH
        return 1
    }

    // 12345.67 -> 12345 like BTC
    return 0
}

function formatPrice(value?: Big, precision: number = 0) {
    if (value) {
        return value.toFixed(precision)
    } else {
        return "0"
    }
}
export const LiquidityCard = ({ liquidity }: LiquidityCardProps) => {
    const {
        baseSymbol,
        quoteSymbol,
        tickerSymbol,
        rangeType,
        baseRatio,
        feeStr,
        amountBaseStr,
        amountQuoteStr,
        amountTotalAsQuoteStr,
        basePercentageStr,
        quotePercentageStr,
    } = useLiquidityDetailStrings(liquidity)

    const { status: marketStatus } = useMarket(tickerSymbol)
    const isIncreaseEnabled = marketStatus === MarketStatus.ACTIVE
    const isRemoveEnabled = marketStatus === MarketStatus.ACTIVE || marketStatus === MarketStatus.CLOSED

    const makerPositionImpermanent = usePositionMakerImpermanent(liquidity)

    const { open: openModalLiquidityAdd } = useModal(ModalType.LIQUIDITY_ADD)
    const { open: openModalLiquidityRemove } = useModal(ModalType.LIQUIDITY_REMOVE)

    const rangeSize = useBreakpointValue({ base: "xl", lg: "2xl" })
    const statsVariant = useBreakpointValue({ base: "left", md: "top" })
    const statsSize = useBreakpointValue({ base: "sm", md: "lg" })

    const { lowerTickPrice, upperTickPrice } = liquidity

    const digitsOfPrice = lowerTickPrice.toString().split(".")[0].length
    const precision = lowerTickPrice ? getPrecisionByDigits(digitsOfPrice) : 0

    const lowerTickPriceStr = lowerTickPrice ? formatPrice(lowerTickPrice, precision) : "0"
    const upperTickPriceStr = upperTickPrice ? formatPrice(upperTickPrice, precision) : "0"

    const RangeIndicator = useMemo(
        () => (
            <Slider
                aria-label="slider-ex-1"
                colorScheme="gray"
                bg="blue"
                value={baseRatio ? (1 - baseRatio.toNumber()) * 100 : undefined}
                isReadOnly
            >
                <SliderTrack
                    bg={rangeType ? (rangeType === RangeType.RANGE_INSIDE ? "green.800" : "red.800") : "gray.600"}
                >
                    <SliderFilledTrack
                        bg={rangeType ? (rangeType === RangeType.RANGE_INSIDE ? "green.800" : "red.800") : "gray.600"}
                    />
                </SliderTrack>
                <SliderThumb
                    h="4px"
                    opacity={rangeType ? (rangeType === RangeType.RANGE_INSIDE ? 1 : 1) : 0.3}
                    bg={rangeType ? (rangeType === RangeType.RANGE_INSIDE ? "green.300" : "red.300") : undefined}
                />
            </Slider>
        ),
        [rangeType, baseRatio],
    )

    return (
        <MotionVStack
            initial={{ backgroundColor: "#191B1B" }}
            animate={{
                backgroundColor: rangeType
                    ? rangeType === RangeType.RANGE_INSIDE
                        ? ["#3D7558", "#24392F"]
                        : ["#843C3F", "#492D2E"]
                    : "#191B1B",
            }}
            borderRadius="2xl"
            spacing={4}
            p={6}
            align="stretch"
        >
            <Box w="full" position="relative">
                <Stats
                    label={
                        rangeType ? (rangeType === RangeType.RANGE_INSIDE ? "In Range" : "Out of Range") : "Price Range"
                    }
                    labelColor={
                        rangeType ? (rangeType === RangeType.RANGE_INSIDE ? "green.400" : "red.400") : undefined
                    }
                    size={rangeSize}
                    textTransform="lowercase"
                    numberComponent={
                        <Tooltip label={`Range from ${lowerTickPriceStr} to ${upperTickPriceStr}.`}>
                            <HStack w="full" spacing={2} overflow={"hidden"} cursor="help">
                                <Text>${lowerTickPriceStr}</Text>
                                <Box
                                    justifyContent={"center"}
                                    flexGrow={1}
                                    px={2}
                                    display={{ base: "none", md: "flex" }}
                                >
                                    {RangeIndicator}
                                </Box>
                                <Box display={{ base: "block", md: "none" }} flexGrow={1} />
                                <Text>${upperTickPriceStr}</Text>
                            </HStack>
                        </Tooltip>
                    }
                />
            </Box>

            <Box display={{ base: "block", md: "none" }}>{RangeIndicator}</Box>

            <Accordion allowToggle w="100%">
                <AccordionItem bg="transparent" border="none">
                    <AccordionButton _hover={{ background: "none" }} bg="transparent" px={0}>
                        <SimpleGrid columns={{ base: 1, lg: 2 }} flex="1" spacing={1} textAlign="left" mr={3}>
                            <Stats
                                variant={statsVariant}
                                size={statsSize}
                                icon={<Icon color="perpetual.500" as={RiContrastDrop2Fill} />}
                                width={"100%"}
                                label="Liquidity" // TODO: Does user understand `liquidity`?
                                number={"$" + amountTotalAsQuoteStr}
                            />

                            <Stats
                                variant={statsVariant}
                                size={statsSize}
                                icon={<Icon color="yellow.500" as={RiBubbleChartLine} />}
                                label="Fees"
                                number={"$" + feeStr}
                            />
                        </SimpleGrid>
                        <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel px={0} pt={4} pb={2}>
                        <Divider />
                        <Text mt={3} variant="label">
                            Token Allocation
                        </Text>
                        <HStack mt={3} w="full" align={"stretch"}>
                            <Stats
                                flexGrow={1}
                                size="sm"
                                label={quoteSymbol}
                                number={amountQuoteStr}
                                helper={quotePercentageStr + "%"}
                            />
                            <Stats
                                flexGrow={1}
                                size="sm"
                                label={baseSymbol}
                                number={amountBaseStr}
                                helper={basePercentageStr + "%"}
                            />
                        </HStack>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
            <Divider />
            <ButtonGroup size="sm" isAttached variant="outline">
                <Button
                    onClick={() => openModalLiquidityAdd({ baseSymbol, tickerSymbol, existingLiquidity: liquidity })}
                    mr="-px"
                    leftIcon={<AddIcon />}
                    isDisabled={!isIncreaseEnabled}
                >
                    Increase
                </Button>
                <Button
                    onClick={() =>
                        openModalLiquidityRemove({
                            tickerSymbol,
                            existingLiquidity: liquidity,
                            makerPositionImpermanent,
                        })
                    }
                    leftIcon={<MinusIcon />}
                    isDisabled={!isRemoveEnabled}
                >
                    Remove
                </Button>
            </ButtonGroup>
        </MotionVStack>
    )
}
